/**
 * Attribute expressions for Select by attribute.
 *
 * Kept as structured clauses rather than a SQL string, for the same reason the
 * label filter is: the same expression has to become a client-side predicate
 * now and a server-side `where` / `CQL_FILTER` later, and a structured form
 * converts safely to both. A free-text box would need a parser to do the same
 * job, and would hand user text straight to a service.
 *
 * Pure functions only — no React, no services — so the selection logic can be
 * tested on its own and reused by the Query and Filter tools later.
 */

export type AttributeOperator =
  | 'equals'
  | 'notEquals'
  | 'greater'
  | 'greaterOrEqual'
  | 'less'
  | 'lessOrEqual'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'isBlank'
  | 'isNotBlank';

export interface AttributeClause {
  id: string;
  field: string;
  operator: AttributeOperator;
  value: string;
  /** How this clause joins the ones before it. Ignored on the first clause. */
  conjunction: 'and' | 'or';
}

export const ATTRIBUTE_OPERATORS: {
  id: AttributeOperator;
  label: string;
  /** False for the two null tests, whose value box is hidden. */
  needsValue: boolean;
  /** Shown in the readable summary. */
  symbol: string;
}[] = [
  { id: 'equals', label: 'is', needsValue: true, symbol: '=' },
  { id: 'notEquals', label: 'is not', needsValue: true, symbol: '≠' },
  { id: 'greater', label: 'is greater than', needsValue: true, symbol: '>' },
  { id: 'greaterOrEqual', label: 'is at least', needsValue: true, symbol: '≥' },
  { id: 'less', label: 'is less than', needsValue: true, symbol: '<' },
  { id: 'lessOrEqual', label: 'is at most', needsValue: true, symbol: '≤' },
  { id: 'contains', label: 'contains', needsValue: true, symbol: 'contains' },
  { id: 'startsWith', label: 'starts with', needsValue: true, symbol: 'starts with' },
  { id: 'endsWith', label: 'ends with', needsValue: true, symbol: 'ends with' },
  { id: 'isBlank', label: 'is empty', needsValue: false, symbol: 'is empty' },
  { id: 'isNotBlank', label: 'is not empty', needsValue: false, symbol: 'is not empty' },
];

export function operatorNeedsValue(operator: AttributeOperator): boolean {
  return ATTRIBUTE_OPERATORS.find((entry) => entry.id === operator)?.needsValue ?? true;
}

/** A clause the user has actually finished filling in. */
export function clauseIsComplete(clause: AttributeClause): boolean {
  if (!clause.field) return false;
  return operatorNeedsValue(clause.operator) ? clause.value.trim() !== '' : true;
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  return String(value).trim() === '';
}

/**
 * Y/M/D[/H/M/S] pulled directly out of the text by regex — never through
 * `Date.parse`/`new Date(string)`.
 *
 * Those read an ISO date-only string ("2024-05-14") as UTC midnight but a
 * legacy slash format ("1/8/2026") as LOCAL midnight — the ECMAScript spec
 * mandates the first and leaves the second implementation-defined, and V8
 * (every browser this app runs in) treats them differently. Converting the
 * second case back to a day string with a UTC-based formatter then shifts
 * it by a day on any machine whose timezone offset is non-zero — exactly
 * what made the Time Slider look like it was filtering nothing at all for
 * data exported as "1/8/2026" when the server/browser runs ahead of UTC
 * (India included). Reading the digits straight from the string and
 * building the timestamp with `Date.UTC` sidesteps both engine string
 * parsing and local-time conversion entirely, so the result is identical
 * wherever this runs.
 */
function extractDateComponents(
  text: string,
): [year: number, month: number, day: number, hour: number, minute: number, second: number] | null {
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(text);
  if (m) return [+m[1], +m[2], +m[3], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0)];

  // Month/day/year — the same reading `new Date("1/8/2026")` gives in every
  // major browser, kept for consistency with anywhere else in the app that
  // still reads such a value the native way.
  m = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(text);
  if (m) return [+m[3], +m[1], +m[2], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0)];

  return null;
}

/**
 * A value that looks like a date, as epoch milliseconds (UTC) — or `null`
 * if it does not. Exported so `tools/toolFields.ts`'s own date-field
 * discovery uses the exact same rule `compare()` below does; the two
 * drifting apart is how a field gets detected as a date but then compared
 * as text (or vice versa).
 *
 * Deliberately narrow: a bare number only counts in a plausible date window
 * (1970-2100, i.e. a real epoch-ms timestamp, not a diameter or an id), and
 * a string has to match one of `extractDateComponents`'s shapes.
 */
export function parseLooseDate(value: unknown): number | null {
  const MIN_MS = Date.UTC(1970, 0, 1);
  const MAX_MS = Date.UTC(2100, 0, 1);
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= MIN_MS && value <= MAX_MS ? value : null;
  }
  if (typeof value !== 'string') return null;
  const parts = extractDateComponents(value.trim());
  if (!parts) return null;
  const [year, month, day, hour, minute, second] = parts;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const ms = Date.UTC(year, month - 1, day, hour, minute, second);
  return ms >= MIN_MS && ms <= MAX_MS ? ms : null;
}

/**
 * Numeric comparison when both sides are numbers, DATE comparison when both
 * sides look like one (in whatever format each happens to be written in —
 * see `parseLooseDate`), text comparison otherwise.
 *
 * The date branch exists because a column exported as "1/8/2026" sorts
 * correctly as a calendar date but NOT as text against an ISO cutoff like
 * "2026-01-08" — every such value starts with "1/", which text-compares as
 * less than "2026-…" regardless of the actual date, so every row passed a
 * `<=` filter no matter where the cutoff was. This is what the Time Slider
 * tool's local-layer filtering depends on.
 *
 * The numeric case is what makes `diameter > 63` behave on a service that
 * publishes diameters as strings — comparing "630" to "63" as text would say
 * the wrong thing, and a diameter column with one "n/a" in it must not turn
 * the whole comparison numeric-only and silently drop rows.
 */
function compare(raw: unknown, input: string): number | null {
  const left = Number(raw);
  const right = Number(input);
  const bothNumeric =
    raw !== null &&
    raw !== undefined &&
    String(raw).trim() !== '' &&
    input.trim() !== '' &&
    Number.isFinite(left) &&
    Number.isFinite(right);

  if (bothNumeric) return left === right ? 0 : left < right ? -1 : 1;

  const leftDate = parseLooseDate(raw);
  const rightDate = parseLooseDate(input);
  if (leftDate !== null && rightDate !== null) {
    return leftDate === rightDate ? 0 : leftDate < rightDate ? -1 : 1;
  }

  const leftText = String(raw ?? '').trim().toLowerCase();
  const rightText = input.trim().toLowerCase();
  return leftText === rightText ? 0 : leftText < rightText ? -1 : 1;
}

/** Whether one attribute value satisfies one clause. */
export function evaluateClause(raw: unknown, clause: AttributeClause): boolean {
  switch (clause.operator) {
    case 'isBlank':
      return isBlank(raw);
    case 'isNotBlank':
      return !isBlank(raw);
    default:
      break;
  }

  // A blank value can only ever fail a comparison, and short-circuiting here
  // keeps `contains ""` from matching every empty cell.
  if (isBlank(raw)) return false;

  const text = String(raw).trim().toLowerCase();
  const needle = clause.value.trim().toLowerCase();

  switch (clause.operator) {
    case 'contains':
      return text.includes(needle);
    case 'startsWith':
      return text.startsWith(needle);
    case 'endsWith':
      return text.endsWith(needle);
    default:
      break;
  }

  const order = compare(raw, clause.value);
  if (order === null) return false;

  switch (clause.operator) {
    case 'equals':
      return order === 0;
    case 'notEquals':
      return order !== 0;
    case 'greater':
      return order > 0;
    case 'greaterOrEqual':
      return order >= 0;
    case 'less':
      return order < 0;
    case 'lessOrEqual':
      return order <= 0;
    default:
      return false;
  }
}

/**
 * Whether a feature satisfies the whole expression.
 *
 * Clauses combine strictly LEFT TO RIGHT — `A or B and C` reads as
 * `(A or B) and C`, not the usual precedence where AND binds tighter. This
 * matches how the expression is written and read in the UI, one row at a time,
 * and is the same rule ArcGIS's own clause builder uses. Incomplete clauses are
 * ignored so a half-typed row never empties the selection.
 */
export function matchesClauses(
  properties: Record<string, unknown>,
  clauses: AttributeClause[],
): boolean {
  const usable = clauses.filter(clauseIsComplete);
  if (usable.length === 0) return false;

  let result = evaluateClause(properties?.[usable[0].field], usable[0]);
  for (const clause of usable.slice(1)) {
    const value = evaluateClause(properties?.[clause.field], clause);
    result = clause.conjunction === 'or' ? result || value : result && value;
  }
  return result;
}

/** A one-line readable form of the expression, for the UI to echo back. */
export function describeClauses(clauses: AttributeClause[]): string {
  return clauses
    .filter(clauseIsComplete)
    .map((clause, index) => {
      const symbol =
        ATTRIBUTE_OPERATORS.find((entry) => entry.id === clause.operator)?.symbol ?? '=';
      const body = operatorNeedsValue(clause.operator)
        ? `${clause.field} ${symbol} ${clause.value.trim()}`
        : `${clause.field} ${symbol}`;
      return index === 0 ? body : `${clause.conjunction.toUpperCase()} ${body}`;
    })
    .join(' ');
}

/**
 * Distinct values of one field across the loaded records, for the value picker.
 *
 * Capped: a picker with ten thousand entries helps nobody and freezes the
 * window while it renders.
 */
export function distinctValues(
  records: { properties: Record<string, unknown> }[],
  field: string,
  limit = 200,
): string[] {
  if (!field) return [];
  const values = new Set<string>();
  for (const record of records) {
    const raw = record.properties?.[field];
    if (isBlank(raw)) continue;
    values.add(String(raw).trim());
    if (values.size >= limit) break;
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/* ────────────────────────── Server-side expressions ───────────────────────
 * The conversion this file was written for: the same structured clauses become
 * a client-side predicate (matchesClauses, above) or a server-side filter.
 *
 * Two dialects, because the two services genuinely differ:
 *  - `sql`  ArcGIS `where` — standard SQL-92, single-quoted literals.
 *  - `cql`  GeoServer `CQL_FILTER` — close to SQL, but its own grammar. It has
 *           no `LIKE '%x%'` shortcut worth relying on across versions, so
 *           `contains` becomes `strToLowerCase(field) LIKE '%x%'` only where
 *           that is safe; here it stays a plain LIKE, which GeoServer accepts.
 *
 * Values are ESCAPED, not interpolated raw. A name with an apostrophe —
 * "St John's Road" — would otherwise terminate the literal early and hand the
 * service a broken filter, or worse.
 */
export type FilterDialect = 'sql' | 'cql';

/** Doubles single quotes, the escape both dialects use inside a literal. */
function quote(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * Quoting is decided by the FIELD's type, never by how the value looks.
 * Checked against a live service, both of these are errors:
 *
 *   ADDRNUM = 10      ADDRNUM is a String column
 *   OBJECTID = '10'   OBJECTID is numeric
 *
 * The value "10" is identical in both, so guessing from it is wrong half the
 * time. An unknown field is quoted, which is the safer half: a quoted number
 * is usually cast, an unquoted string never is.
 */
function literalFor(
  clause: AttributeClause,
  dialect: FilterDialect,
  numericFields: Set<string>,
  dateFields: Set<string>,
): string {
  const field = clause.field;
  const raw = clause.value.trim();
  if (numericFields.has(field)) return raw;
  // GeoServer's ECQL parses a plain quoted ISO date string against a
  // date/timestamp column on its own — no change needed there. ArcGIS's
  // `where` clause is stricter about comparing a bare string to a Date
  // column, so the SQL dialect wraps it as a DATE literal instead.
  if (dateFields.has(field) && dialect === 'sql') return `DATE ${quote(raw)}`;
  return quote(raw);
}

function clauseToExpression(
  clause: AttributeClause,
  dialect: FilterDialect,
  numericFields: Set<string>,
  dateFields: Set<string> = new Set(),
): string | null {
  const field = clause.field;
  const raw = clause.value.trim();
  const literal = literalFor(clause, dialect, numericFields, dateFields);

  switch (clause.operator) {
    case 'equals':
      return `${field} = ${literal}`;
    case 'notEquals':
      return `${field} <> ${literal}`;
    case 'greater':
      return `${field} > ${literal}`;
    case 'greaterOrEqual':
      return `${field} >= ${literal}`;
    case 'less':
      return `${field} < ${literal}`;
    case 'lessOrEqual':
      return `${field} <= ${literal}`;
    case 'contains':
      return `${field} LIKE ${quote(`%${raw}%`)}`;
    case 'startsWith':
      return `${field} LIKE ${quote(`${raw}%`)}`;
    case 'endsWith':
      return `${field} LIKE ${quote(`%${raw}`)}`;
    case 'isBlank':
      return `${field} IS NULL`;
    case 'isNotBlank':
      return `${field} IS NOT NULL`;
    default:
      return null;
  }
}

/**
 * The NULL-safe negation of one clause — what "doesn't match" actually
 * means for a feature whose field is blank.
 *
 * Plain SQL/CQL negation is NOT enough: comparisons use three-valued logic,
 * so for a feature where `field IS NULL`, BOTH `field = 'x'` AND its literal
 * negation `field <> 'x'` evaluate to UNKNOWN, not TRUE — meaning a blank
 * feature would silently drop out of an inverted filter too, even though
 * "doesn't equal x" is exactly what a blank value is. Each comparison here
 * is therefore paired with an explicit `OR field IS NULL`, so a blank
 * feature is included in the inverted result the way a user actually means
 * "except these". `isBlank`/`isNotBlank` need no such pairing — they already
 * partition NULL vs. not-NULL exactly, with nothing left over.
 */
function negatedClauseToExpression(
  clause: AttributeClause,
  dialect: FilterDialect,
  numericFields: Set<string>,
  dateFields: Set<string> = new Set(),
): string | null {
  const field = clause.field;
  const raw = clause.value.trim();
  const literal = literalFor(clause, dialect, numericFields, dateFields);
  const nullSafe = (expression: string) => `(${expression} OR ${field} IS NULL)`;

  switch (clause.operator) {
    case 'equals':
      return nullSafe(`${field} <> ${literal}`);
    case 'notEquals':
      return nullSafe(`${field} = ${literal}`);
    case 'greater':
      return nullSafe(`${field} <= ${literal}`);
    case 'greaterOrEqual':
      return nullSafe(`${field} < ${literal}`);
    case 'less':
      return nullSafe(`${field} >= ${literal}`);
    case 'lessOrEqual':
      return nullSafe(`${field} > ${literal}`);
    case 'contains':
      return nullSafe(`${field} NOT LIKE ${quote(`%${raw}%`)}`);
    case 'startsWith':
      return nullSafe(`${field} NOT LIKE ${quote(`${raw}%`)}`);
    case 'endsWith':
      return nullSafe(`${field} NOT LIKE ${quote(`%${raw}`)}`);
    case 'isBlank':
      return `${field} IS NOT NULL`;
    case 'isNotBlank':
      return `${field} IS NULL`;
    default:
      return null;
  }
}

/**
 * Turns finished clauses into a filter the service will accept.
 *
 * Returns an empty string when nothing is usable, which every caller should
 * read as "no filter" — never as "match nothing".
 *
 * Inverted, this negates EACH clause individually (NULL-safe — see
 * `negatedClauseToExpression`) and flips AND/OR between them (De Morgan),
 * rather than wrapping the whole joined expression in one `NOT (...)` —
 * the earlier approach was exact SQL, but SQL's three-valued NULL logic
 * means a flat `NOT (...)` around the joined expression silently excludes
 * every feature with a blank value in any field the clauses touch, in both
 * the matched AND the inverted result. Per-clause negation keeps each
 * blank feature in exactly the one of those two results it belongs in.
 */
export function buildFilterExpression(
  clauses: AttributeClause[],
  dialect: FilterDialect,
  invert = false,
  /** Fields the service publishes as numbers. Anything absent is quoted. */
  numericFields: Set<string> = new Set(),
  /** Fields the service publishes as a date/dateTime. See `clauseToExpression`. */
  dateFields: Set<string> = new Set(),
): string {
  const toExpression = invert ? negatedClauseToExpression : clauseToExpression;
  const parts = clauses
    .filter(clauseIsComplete)
    .map((clause, index) => {
      const body = toExpression(clause, dialect, numericFields, dateFields);
      if (!body) return null;
      if (index === 0) return body;
      const joinsWithOr = invert ? clause.conjunction !== 'or' : clause.conjunction === 'or';
      return `${joinsWithOr ? 'OR' : 'AND'} ${body}`;
    })
    .filter((part): part is string => part !== null);

  return parts.join(' ');
}
